// storedFile.js - #536. Turning a stored photo reference into an address the
// browser can actually load, WITHOUT that address working for the whole internet.
//
// THE PROBLEM THIS EXISTS FOR, in plain terms: every photograph anybody at D&R
// has taken into Stede - a repair receipt, a packing slip, an item picture - was
// saved somewhere anyone could reach with no account and no password. Proven at
// 79.12 against production: an anonymous request listed 21 real documents by name
// and one was then downloaded with no key of any kind. The buckets become private
// and every read goes through here instead.
//
// THE ONE THING THAT MAKES THIS SAFE TO SHIP, and it was measured rather than
// assumed: A SIGNED ADDRESS WORKS WHILE THE BUCKET IS STILL PUBLIC. So this code
// ships first and keeps every screen working exactly as it does today, and the
// buckets are closed afterwards. There is no moment where a photo is broken.
//
// AND THE REASON THIS TAKES *EITHER* SHAPE RATHER THAN ONE: the app has been
// storing two different things all along, and neither can be migrated away
// safely in the same pass.
//
//   * The inventory side stores the WHOLE web address - `getPublicUrl()`'s
//     output went straight into `packing_slip_url`, `image_url` and `photo_url`.
//   * The tools side stores a BARE PATH - `uploadPhoto()` returns `data.path`
//     and the address was rebuilt at render.
//
// Both are already in the database on both databases. Rewriting those rows is a
// data migration with nothing to gain, so this reads both forever instead. That
// is the compatibility layer, and it is the whole reason nothing breaks.

import { supabase } from './supabase';

// How long a signed address stays good. One hour: long enough that a person can
// open a record, read it, and come back to it, and short enough that an address
// copied out of a browser bar is worthless by the time it is pasted anywhere.
// This is the number to change if somebody reports a photo going blank on a page
// left open all afternoon.
export const DEFAULT_EXPIRY_SECONDS = 3600;

export const INVENTORY_BUCKET = 'inventory-assets';
export const TOOL_BUCKET = 'tool-assets';

// Pull the object's path out of whatever was stored.
//
// Returns null rather than a guess when the value cannot be understood, because
// a wrong path produces a broken image and a null produces the honest "no photo"
// state the screens already handle. #504's rule: do not report success you have
// not got.
export function storagePathOf(stored, bucket) {
  if (!stored || typeof stored !== 'string') return null;
  const value = stored.trim();
  if (!value) return null;

  // Shape 1 - a whole web address written by getPublicUrl(), or an already
  // signed one. Both carry the bucket name in a fixed position, so the path is
  // everything after it. The query string is dropped: a signed address carries
  // its token there and re-signing an old token would produce nonsense.
  if (value.includes('://')) {
    for (const marker of [`/object/public/${bucket}/`, `/object/sign/${bucket}/`]) {
      const at = value.indexOf(marker);
      if (at !== -1) {
        const rest = value.slice(at + marker.length).split('?')[0];
        return rest ? decodeURIComponent(rest) : null;
      }
    }
    // A web address that is NOT one of ours. Never hand this to the signer - it
    // would produce an address pointing at a file that is not there.
    return null;
  }

  // Shape 2 - a bare path. Tolerate a leading slash and a redundant bucket
  // prefix, both of which are easy to write by hand and neither of which the
  // storage API accepts.
  let path = value.replace(/^\/+/, '');
  if (path.startsWith(`${bucket}/`)) path = path.slice(bucket.length + 1);
  return path || null;
}

// Ask the storage service for a time-limited address for one stored file.
//
// Never throws. Every caller here is drawing a screen, and a screen that throws
// on a missing photo takes the whole page with it - which is the temporal-dead-
// zone failure 79.12 shipped twice and is exactly what this must not reintroduce.
export async function signedUrlFor(stored, bucket, expiresIn = DEFAULT_EXPIRY_SECONDS) {
  const path = storagePathOf(stored, bucket);
  if (!path) return null;
  try {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, expiresIn);
    if (error) return null;
    return data?.signedUrl || null;
  } catch {
    return null;
  }
}

// Open a stored file in a new tab. Used where a screen previously carried a
// plain link, because a link needs its address before the person clicks and a
// signed address is better fetched AT the click - fewer requests, and nothing
// sitting in the page for an hour waiting to be copied.
export async function openStoredFile(stored, bucket, expiresIn = DEFAULT_EXPIRY_SECONDS) {
  const url = await signedUrlFor(stored, bucket, expiresIn);
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
  return !!url;
}
